import { useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Textarea } from '@/components/ui/textarea';

import { getAgentModelName } from '../lib/agentModels';
import { PROMPT_LABEL_META } from '../lib/promptLabels';
import type { AgentModelOptionsResponse } from '../services/projects.api';
import AgentModelField from './AgentModelField';
import ModelChangeChoice from './ModelChangeChoice';

export type PublishableLabel = 'production' | 'latest';

const MIN_REASON_LENGTH = 10;

const LABEL_CHOICES: { value: PublishableLabel; hint: string }[] = [
  { value: 'latest', hint: 'Solo Demo Chat y Centro de Testing. Para probar.' },
  { value: 'production', hint: 'Responde a los clientes reales.' },
];

export interface PublishVersionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** e.g. "Prompt Común · v6" */
  subtitle: string;
  defaultPromptLabel?: PublishableLabel;
  isPublishing?: boolean;
  /** Undefined when the model list could not load: the version then keeps the production model. */
  modelOptions: AgentModelOptionsResponse | undefined;
  /** Model selected when the dialog opens: the base version's model test, else the production model. */
  defaultModel: string | undefined;
  /** Master versions with subprojects: applying the model also moves every subproject. */
  movesSubprojects?: boolean;
  onPublish: (input: {
    changeReason: string;
    promptLabel: PublishableLabel;
    /**
     * Model CS chose for the version, or null to keep the production model. Null lets the backend use the model that
     * runs now: the production model this page loaded can be stale if someone changed it meanwhile.
     */
    model: string | null;
  }) => Promise<void> | void;
}

const PublishVersionDialog = ({
  open,
  onOpenChange,
  subtitle,
  defaultPromptLabel = 'latest',
  isPublishing = false,
  modelOptions,
  defaultModel,
  movesSubprojects,
  onPublish,
}: PublishVersionDialogProps): JSX.Element => {
  const [changeReason, setChangeReason] = useState('');
  const [promptLabel, setPromptLabel] = useState<PublishableLabel>(defaultPromptLabel);
  // Follows the default until CS picks a model, so a model list that loads after the dialog opens still applies.
  const [pickedModel, setModel] = useState<string | null>(null);
  const model = pickedModel ?? defaultModel ?? '';
  const [applyModel, setApplyModel] = useState(false);

  const isModelDifferent = !!modelOptions && model !== modelOptions.currentModel;
  const keepsProductionModel = !isModelDifferent || (promptLabel === 'production' && !applyModel);

  const reason = changeReason.trim();
  const isReasonInvalid = changeReason.length > 0 && reason.length < MIN_REASON_LENGTH;
  const canPublish = reason.length >= MIN_REASON_LENGTH && !isPublishing;

  const handlePublish = async (): Promise<void> => {
    if (!canPublish) return;
    await onPublish({ changeReason: reason, promptLabel, model: keepsProductionModel ? null : model });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[min(520px,calc(100vw-2rem))] max-w-none gap-0 overflow-hidden p-0 sm:max-w-none">
        <DialogHeader className="border-b border-border-subtle px-6 py-4">
          <DialogTitle className="text-base font-semibold">Publicar versión</DialogTitle>
          <DialogDescription className="text-xs text-text-muted">{subtitle}</DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-5 px-6 py-5">
          <div className="flex flex-col gap-2">
            <Label>Publicar como</Label>
            <RadioGroup
              value={promptLabel}
              onValueChange={(value) => setPromptLabel(value as PublishableLabel)}
              className="grid grid-cols-2 gap-2"
            >
              {LABEL_CHOICES.map(({ value, hint }) => {
                const meta = PROMPT_LABEL_META[value];
                return (
                  <Label
                    key={value}
                    htmlFor={`publish-label-${value}`}
                    className="flex cursor-pointer items-start gap-2.5 rounded-md border border-border-subtle px-3 py-2.5 font-normal hover:border-indigo-200 has-[[data-state=checked]]:border-indigo-300 has-[[data-state=checked]]:bg-indigo-50/50"
                  >
                    <RadioGroupItem value={value} id={`publish-label-${value}`} className="mt-0.5" />
                    <span className="flex flex-col gap-1">
                      <Badge className={meta.chip}>{meta.text}</Badge>
                      <span className="text-[11px] leading-snug text-text-muted">{hint}</span>
                    </span>
                  </Label>
                );
              })}
            </RadioGroup>
          </div>

          {modelOptions ? (
            <AgentModelField
              id="publish-version-model"
              value={model}
              onChange={setModel}
              modelOptions={modelOptions}
              showDemoChatNote={promptLabel === 'latest'}
            />
          ) : (
            <p className="m-0 text-xs text-text-muted">Modelos no disponibles. Se mantiene el modelo actual.</p>
          )}

          {modelOptions && isModelDifferent && promptLabel === 'production' && (
            <ModelChangeChoice
              currentModelName={getAgentModelName(modelOptions.currentModel, modelOptions.options)}
              versionModelName={getAgentModelName(model, modelOptions.options)}
              applyModel={applyModel}
              onApplyModelChange={setApplyModel}
              movesSubprojects={movesSubprojects}
            />
          )}

          <div className="flex flex-col gap-2">
            <Label htmlFor="publish-version-reason">
              Nota de versión
              <span className="font-normal text-text-muted">(requerida)</span>
            </Label>
            <Textarea
              id="publish-version-reason"
              value={changeReason}
              onChange={(e) => setChangeReason(e.target.value)}
              aria-invalid={isReasonInvalid}
              placeholder="Qué cambiaste y por qué. Queda en el historial."
              rows={3}
              className="max-h-32 resize-none overflow-auto rounded-lg bg-gray-50 text-xs [field-sizing:fixed]"
            />
            {isReasonInvalid && (
              <span className="text-[11px] text-destructive">Mínimo {MIN_REASON_LENGTH} caracteres.</span>
            )}
          </div>
        </div>

        <DialogFooter className="m-0 gap-2 rounded-none border-t border-border-subtle bg-transparent px-6 py-4">
          <Button type="button" size="sm" variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button
            type="button"
            size="sm"
            disabled={!canPublish}
            onClick={handlePublish}
            className="bg-gray-900 text-white hover:bg-gray-800"
          >
            {isPublishing ? 'Publicando…' : 'Publicar versión'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PublishVersionDialog;
